import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { PromoBundleBuyTypes } from '../../constants';
import { isArrayObjectEqual } from '../../utils/index';

/**
 * Helpers behind the order form's "add bundle" shortcut: they turn a bundle
 * promo's `buys` into the order lines that satisfy it.
 *
 * Kept free of React so the matching can be exercised directly against the
 * promo engine's own rules, which are what ultimately accept or reject the
 * populated order.
 */

/**
 * Label a catalogue variant the way the order table shows it, so the admin
 * recognises what they picked once the line lands in the order.
 * @param {Object} variant
 * @returns {string}
 */
export const variantLabel = (variant) => {
  const subValues = (variant.subVariants || [])
    .filter((sub) => sub.value)
    .map((sub) => sub.value);
  return subValues.length
    ? `${variant.type} - ${subValues.join(' / ')}`
    : variant.type;
};

/**
 * Find the catalogue variant a promo's pinned buy option refers to.
 * Buy options store `{ id, variant, subVariants }` rather than the variant
 * itself, so the live price, sku and stock have to be looked up.
 * @param {Object} product catalogue product
 * @param {Object} option promo buy option
 * @returns {Object|null}
 */
export const resolvePinnedVariant = (product, option) => {
  if (!product.variants || !product.variants.length) return null;
  if (option.subVariants && option.subVariants.length) {
    return (
      product.variants.find(
        (v) =>
          v.type === option.variant &&
          isArrayObjectEqual(v.subVariants, option.subVariants, [
            'key',
            'assetId',
          ])
      ) || null
    );
  }
  return product.variants.find((v) => v.type === option.variant) || null;
};

/**
 * Turn one `bundle.buys` entry into the choices an admin can resolve.
 *
 * A `products` buy lists fully pinned variants as alternatives, so they are
 * regrouped by product and the admin picks product then variation. A
 * `collections` buy leaves both open, so every catalogue product in those
 * collections is offered with all of its variants.
 * @param {Object} buy a `bundle.buys` entry
 * @param {Array} products catalogue
 * @returns {Array} `[{ id, name, variants }]`
 */
export const buildRowOptions = (buy, products) => {
  if (buy.type === PromoBundleBuyTypes.PRODUCTS) {
    const grouped = [];
    (buy.products || []).forEach((option) => {
      const product = products.find((p) => p.id === option.id);
      // a promo can outlive the product it names
      if (!product) return;
      const variant = resolvePinnedVariant(product, option);
      if (!variant) return;
      let entry = grouped.find((g) => g.id === product.id);
      if (!entry) {
        entry = { id: product.id, name: product.name, variants: [] };
        grouped.push(entry);
      }
      if (!entry.variants.includes(variant)) entry.variants.push(variant);
    });
    return grouped;
  }

  const collectionNames = (buy.collections || []).map((c) => c.name);
  return products
    .filter(
      (p) =>
        collectionNames.includes(p.collection) &&
        p.variants &&
        p.variants.length
    )
    .map((p) => ({ id: p.id, name: p.name, variants: [...p.variants] }));
};

/**
 * Describe what a buy entry asks for, for the picker's row heading.
 * @param {Object} buy
 * @returns {string}
 */
export const buyLabel = (buy) => {
  const qty = buy.qty || 1;
  if (buy.type === PromoBundleBuyTypes.PRODUCTS)
    return `Buy ${qty} of the following`;
  const names = (buy.collections || []).map((c) => c.name).join(', ');
  return `Buy ${qty} from ${names || 'collection'}`;
};

/**
 * Build the order line for one resolved bundle item.
 *
 * Deliberately mirrors the shape `ProductModal` produces, plus the markers
 * that tie the line to its bundle. It never sets `promoCode` — that marks a
 * free redeem line, which the promo engine excludes from buy matching.
 * @param {Object} product catalogue product
 * @param {Object} variant catalogue variant
 * @param {number} qty
 * @param {Object} tags `{ bundleGroupId, bundleCode }`
 * @returns {Object} order line
 */
export const buildBundleLine = (product, variant, qty, tags) => {
  const media = (product.media || []).find((m) => m.default);
  const unitPrice = parseFloat(variant.prices[0].amount);
  const line = {
    isNew: true,
    uuid: uuidv4(),
    bundleGroupId: tags.bundleGroupId,
    bundleCode: tags.bundleCode,
    // what the bundle's buy entry requires, so a later qty edit can be
    // checked against it
    bundleMinQty: qty,
    id: product.id,
    name: product.name,
    shippable: product.shippable,
    collection: product.collection,
    variant: variant.type,
    currency: variant.prices[0].currency,
    status: variant.status,
    statusType: variant.statusType,
    sku: variant.sku,
    unitPrice,
    comparePrice: parseFloat(variant.prices[0].compareAmount) || 0,
    image: media ? media.path : '',
    qty,
    subtotal: qty * unitPrice,
    subVariants: variant.subVariants || [],
  };
  if (variant.statusDate)
    line.statusDate = moment.unix(variant.statusDate.seconds).toDate();
  if (variant.statusNote) line.statusNote = variant.statusNote;
  return line;
};

/**
 * Append a line to a bundle's lines, merging it into an identical one.
 *
 * Two buy entries can resolve to the same product and variant; one line with
 * the summed quantity satisfies both and keeps the order readable.
 * @param {Array} lines accumulated lines, mutated
 * @param {Object} line
 */
export const pushBundleLine = (lines, line) => {
  const existing = lines.find(
    (l) =>
      l.id === line.id &&
      l.variant === line.variant &&
      isArrayObjectEqual(l.subVariants, line.subVariants, ['key', 'assetId'])
  );
  if (!existing) {
    lines.push(line);
    return;
  }
  existing.qty += line.qty;
  existing.subtotal = existing.qty * existing.unitPrice;
  // the merged line still only has to clear the larger requirement
  existing.bundleMinQty = Math.max(existing.bundleMinQty, line.qty);
};
